/*
 * Render a critter notifying the user that they've completed the
 * scratchpad (project, challenge, talkthrough)
 *
 */
import {plural, t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {staticUrl} from "@khan/urls";

import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
import type {UserScratchpadModel} from "#scratchpad-base-package/scratchpads.ts";
import type {ScratchpadChallengeModel} from "#scratchpad-challenge-package/scratchpad-challenge.ts";

type Props = {
    // The challenge we are currently facing
    challenge: ScratchpadChallengeModel | null | undefined;
    // Should the critter bubble be shown above the button?
    showAbove: boolean;
    // The UserScratchpad in use on the project or challenge
    userScratchpad: UserScratchpadModel;
};

type State = {
    bubbleClosed: boolean;
};

class CompletionCritter extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            bubbleClosed: false,
        };
    }

    renderCritter(): React.ReactElement<React.ComponentProps<"img">> {
        return (
            <img
                alt=""
                aria-hidden={true}
                className={css(critterStyles.critter)}
                src={staticUrl("/images/scratchpads/cool-critter.png")}
            />
        );
    }

    renderMessage(): React.ReactElement<React.ComponentProps<"div">> {
        const points: number = this.props.userScratchpad
            ? this.props.userScratchpad.get("points")
            : 0;

        return (
            <div className={css(critterStyles.message)}>
                <span className={css(critterStyles.messageHeader)}>
                    {t`Congratulations!`}
                </span>
                {points > 0 && (
                    <span>
                        {plural(points, {
                            one: "You earned # point!",
                            other: "You earned # points!",
                        })}
                    </span>
                )}
            </div>
        );
    }

    renderBubble(): null | React.ReactElement<React.ComponentProps<"div">> {
        if (this.state.bubbleClosed) {
            return null;
        }

        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
            <div
                className={css(
                    critterStyles.bubble,
                    this.props.showAbove && critterStyles.bubbleAbove,
                )}
                onClick={() => this.setState({bubbleClosed: true})}
            >
                <div className={css(critterStyles.inner)}>
                    {this.renderCritter()}
                    {this.renderMessage()}
                </div>
            </div>
        );
    }

    render(): React.ReactNode {
        return (
            <div className={css(critterStyles.wrap)}>{this.renderBubble()}</div>
        );
    }
}

const bubbleDistance = 17;
const bubbleHeight = 80;
const zIndex = 2; // This oughta do it

// Yeah, yeah, I know.
// TODO(ian): Make white when the editor theme is updated.
const white = "#f5f5f5";

const critterStyles = StyleSheet.create({
    bubble: {
        background: white,
        bottom: 70,
        color: "black",
        borderRadius: 10,
        cursor: "pointer",
        display: "inline-block",
        maxWidth: 250,
        minWidth: 200,
        padding: 10,
        position: "absolute",
        right: 0,
        zIndex: zIndex,
    },
    bubbleAbove: {
        bottom: "auto",
        right: 0,
        top: 0 - bubbleDistance - bubbleHeight - 20,
    },
    critter: {
        height: 70,
        marginRight: 10,
    },
    inner: {
        display: "flex",
        flexDirection: "row-reverse",
        justifyContent: "space-around",
    },
    message: {
        display: "flex",
        flexDirection: "column",
    },
    messageHeader: {
        fontWeight: "bold",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    toolbarButton: {
        marginLeft: 16,
    },
    wrap: {
        display: "inline-block",
        position: "relative",
    },
});

export default withBackboneModels((props: Props) => {
    const models: Array<UserScratchpadModel | ScratchpadChallengeModel> = [];

    if (props.userScratchpad) {
        models.push(props.userScratchpad);
    }

    if (props.challenge) {
        models.push(props.challenge);
    }

    return models;
})(CompletionCritter) as React.ComponentType<
    JSX.LibraryManagedAttributes<
        typeof CompletionCritter,
        React.ComponentProps<typeof CompletionCritter>
    >
>;
